import { getApiClient } from "../client";
import { BOT_CONFIG_POPULATE, STRATEGY_POPULATE } from "../populate";
import { BotConfigCreateInputSchema, BotConfigQueryParamsSchema, BotConfigSchema, BotConfigUpdateInputSchema, StrapiIdSchema, StrategyInputSchema, StrategyQueryParamsSchema, StrategySchema, UserGeneralStatsV2Schema, validateInput, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse } from "../utils/parseStrapiResponse";
export const botConfigsEndpoints = {
    async getBotConfig(id, params) {
        const validatedId = validateInput(StrapiIdSchema, id, "botConfigsEndpoints.getBotConfig");
        const populate = params?.populate ?? BOT_CONFIG_POPULATE.FULL;
        const response = await getApiClient().get(`/bot-configs/${validatedId}`, {
            params: { populate },
        });
        return parseStrapiItemResponse(response.data, {
            schema: BotConfigSchema,
            populate,
            context: "botConfigsEndpoints.getBotConfig",
        });
    },
    async getBotConfigs(params) {
        const validated = params
            ? validateInput(BotConfigQueryParamsSchema, params, "botConfigsEndpoints.getBotConfigs")
            : undefined;
        // Use caller's populate if provided and non-empty, otherwise use default
        const populate = params?.populate && params.populate.length > 0 ? params.populate : BOT_CONFIG_POPULATE.LIST;
        // Remove populate from validated to avoid duplicate in params
        const { populate: _omit, ...restValidated } = validated ?? {};
        const response = await getApiClient().get("/bot-configs", {
            params: {
                populate,
                ...restValidated,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: BotConfigSchema,
            populate,
            context: "botConfigsEndpoints.getBotConfigs",
        });
    },
    async getMyBotConfigs(params) {
        const validated = params
            ? validateInput(BotConfigQueryParamsSchema, params, "botConfigsEndpoints.getMyBotConfigs")
            : undefined;
        // Use caller's populate if provided and non-empty, otherwise use default
        const populate = params?.populate && params.populate.length > 0 ? params.populate : BOT_CONFIG_POPULATE.FULL;
        // Extract known params to avoid duplication
        const { populate: _omitPopulate, sort, pagination, publicationState, ...restValidated } = validated ?? {};
        const response = await getApiClient().get("/bot-configs/me", {
            params: {
                populate,
                sort: sort ?? ["disabled:desc"],
                pagination: pagination ?? { pageSize: 100 },
                publicationState: publicationState ?? "preview",
                ...restValidated,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: BotConfigSchema,
            populate,
            context: "botConfigsEndpoints.getMyBotConfigs",
        });
    },
    async createBotConfig(input) {
        const validated = validateInput(BotConfigCreateInputSchema, input, "botConfigsEndpoints.createBotConfig");
        const response = await getApiClient().post("/bot-configs", {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: BotConfigSchema,
            context: "botConfigsEndpoints.createBotConfig",
        });
    },
    async updateBotConfig(id, input) {
        const validatedId = validateInput(StrapiIdSchema, id, "botConfigsEndpoints.updateBotConfig");
        const validated = validateInput(BotConfigUpdateInputSchema, input, "botConfigsEndpoints.updateBotConfig");
        const response = await getApiClient().put(`/bot-configs/${validatedId}`, {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: BotConfigSchema,
            context: "botConfigsEndpoints.updateBotConfig",
        });
    },
    async changePaymentMethod(id, paymentMethodId) {
        const validatedId = validateInput(StrapiIdSchema, id, "botConfigsEndpoints.changePaymentMethod");
        const validatedPaymentMethodId = validateInput(StrapiIdSchema, paymentMethodId, "botConfigsEndpoints.changePaymentMethod");
        const response = await getApiClient().put(`/bot-configs/${validatedId}/payment-method`, {
            data: { paymentMethod: validatedPaymentMethodId },
        });
        return parseStrapiItemResponse(response.data, {
            schema: BotConfigSchema,
            context: "botConfigsEndpoints.changePaymentMethod",
        });
    },
    async getMyGeneralStatsPerCurrency(options) {
        const response = await getApiClient().get("/bot-configs/user/me/general-stats-per-currency", {
            params: { v2: true, ...(options?.includeHistory !== undefined ? { includeHistory: options.includeHistory } : {}) },
        });
        return parseStrapiItemResponse(response.data, {
            schema: UserGeneralStatsV2Schema,
            context: "botConfigsEndpoints.getMyGeneralStatsPerCurrency",
        });
    },
    async getUserGeneralStats(userId, options) {
        const validatedId = validateInput(StrapiIdSchema, userId, "botConfigsEndpoints.getUserGeneralStats");
        const response = await getApiClient().get(`/bot-configs/user/${validatedId}/general-stats-per-currency`, {
            params: { v2: true, ...(options?.includeHistory !== undefined ? { includeHistory: options.includeHistory } : {}) },
        });
        return parseStrapiItemResponse(response.data, {
            schema: UserGeneralStatsV2Schema,
            context: "botConfigsEndpoints.getUserGeneralStats",
        });
    },
};
export const strategiesEndpoints = {
    async getStrategy(id, params) {
        const validatedId = validateInput(StrapiIdSchema, id, "strategiesEndpoints.getStrategy");
        const populate = params?.populate ?? STRATEGY_POPULATE.FULL;
        const response = await getApiClient().get(`/strategies/${validatedId}`, {
            params: { populate },
        });
        return parseStrapiItemResponse(response.data, {
            schema: StrategySchema,
            populate,
            context: "strategiesEndpoints.getStrategy",
        });
    },
    async getStrategies(params) {
        const validated = params
            ? validateInput(StrategyQueryParamsSchema, params, "strategiesEndpoints.getStrategies")
            : undefined;
        const populate = params?.populate ?? STRATEGY_POPULATE.DEFAULT;
        const response = await getApiClient().get("/strategies", {
            params: {
                populate,
                publicationState: validated?.publicationState ?? "preview",
                ...validated,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: StrategySchema,
            populate,
            context: "strategiesEndpoints.getStrategies",
        });
    },
    async createStrategy(input) {
        const validated = validateInput(StrategyInputSchema, input, "strategiesEndpoints.createStrategy");
        const response = await getApiClient().post("/strategies", {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: StrategySchema,
            context: "strategiesEndpoints.createStrategy",
        });
    },
    async updateStrategy(id, input) {
        const validatedId = validateInput(StrapiIdSchema, id, "strategiesEndpoints.updateStrategy");
        const validated = validateInput(StrategyInputSchema, input, "strategiesEndpoints.updateStrategy");
        const response = await getApiClient().put(`/strategies/${validatedId}`, {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: StrategySchema,
            context: "strategiesEndpoints.updateStrategy",
        });
    },
};
